import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ArrowRight, Eye, EyeOff, Layers, Shield, Star } from "lucide-react";
import { APP_NAME } from "@/lib/constants";
import { GoogleAuthButton } from "@/components/auth/GoogleAuthButton";

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      navigate("/");
    } catch (err: any) {
      setError("אימייל או סיסמה שגויים");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex" dir="rtl">

      {/* Right side — branding panel (Apple style) */}
      <div className="hidden lg:flex lg:w-[45%] bg-slate-900 relative overflow-hidden flex-col justify-between p-16">
        <div className="relative z-10">
          <Link to="/" className="text-white text-2xl font-bold tracking-tighter">
            {APP_NAME}
          </Link>
        </div>

        <div className="relative z-10 space-y-8">
          <h2 className="text-5xl font-bold text-white leading-tight tracking-tight">
            המקום שלך ליצור<br />
            ולגלות.
          </h2>
          <p className="text-slate-400 text-lg leading-relaxed max-w-sm font-light">
            הצטרפו לקהילת היוצרים והמשתמשים שמובילה את מהפכת המערכות החכמות בישראל.
          </p>

          <div className="flex gap-8 pt-6">
            {[
              { icon: Layers, label: "מערכות חכמות" },
              { icon: Star, label: "דירוגי קהילה" },
              { icon: Shield, label: "מידע שקוף" },
            ].map((item, i) => (
              <div key={i} className="flex flex-col gap-2">
                <item.icon className="w-5 h-5 text-slate-500" />
                <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-xs text-slate-600 font-medium">
          © {new Date().getFullYear()} {APP_NAME}. All rights reserved.
        </div>

        {/* Abstract background subtle glow */}
        <div className="absolute top-[-10%] right-[-10%] w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-5%] left-[-5%] w-[400px] h-[400px] bg-purple-600/5 rounded-full blur-[100px]" />
      </div>

      {/* Left side — login form */}
      <div className="flex-1 flex flex-col items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-[420px] animate-in fade-in slide-in-from-left-4 duration-700">

          <div className="lg:hidden mb-12">
            <Link to="/" className="text-2xl font-bold text-slate-900 tracking-tighter">
              {APP_NAME}
            </Link>
          </div>

          <Link to="/" className="inline-flex items-center text-sm font-bold text-blue-600 hover:text-blue-700 mb-12 transition-colors">
            <ArrowRight className="h-4 w-4 ms-2" />
            חזרה לדף הבית
          </Link>
          
          <div className="mb-10">
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight mb-3">שלום.</h1>
            <p className="text-slate-500 text-lg">התחברו לחשבון שלכם כדי להמשיך.</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            {error && (
              <Alert variant="destructive" className="bg-red-50 text-red-900 border-none rounded-2xl py-4">
                <AlertDescription className="font-medium">{error}</AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-bold text-slate-400 uppercase tracking-widest mr-1">אימייל</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-14 rounded-2xl border-slate-200 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all shadow-none text-lg px-5"
                placeholder="name@example.com"
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between mr-1">
                <Label htmlFor="password" className="text-xs font-bold text-slate-400 uppercase tracking-widest">סיסמה</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-blue-600 hover:text-blue-700 font-bold"
                >
                  שכחת סיסמה?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-14 rounded-2xl border-slate-200 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all shadow-none pe-14 text-lg px-5"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 end-0 pe-5 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-14 rounded-full bg-slate-900 hover:bg-black text-white font-bold text-lg shadow-xl shadow-slate-100 transition-all active:scale-[0.98] mt-4" 
              disabled={loading}
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "התחברות"}
            </Button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-100" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-400">או</span>
              </div>
            </div>

            <GoogleAuthButton />
          </form>
          
          <div className="mt-12 text-center pt-8 border-t border-slate-100">
            <p className="text-slate-500">
              עדיין אין לך חשבון?{" "}
              <Link to="/register" className="text-blue-600 font-bold hover:underline">
                צרו אחד עכשיו
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;